import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { AppState } from '..';
import {
    catchError,
    filter,
    map,
    of,
    retry,
    switchMap,
    withLatestFrom,
} from 'rxjs';
import { $selectedSiteId } from '../sites/sites.selectors';
import {
    getInsightsDashboardsFailureAction,
    getInsightsDashboardsRequestAction,
    getInsightsDashboardsSuccessAction,
    getInsightsGuestTokenFailureAction,
    getInsightsGuestTokenRequestAction,
    getInsightsGuestTokenSuccessAction,
} from './insights.actions';
import { InsightsDashboards, InsightsGuestToken } from '@myrtls/api-interfaces';
import {
    DASHBOARDS_ENDPOINT,
    INSIGHTS_ENDPOINT,
    SITES_API_URL,
    TOKEN_ENDPOINT,
} from '../../shared/utils/constants';

@Injectable()
export class InsightsEffects {
    getInsightsDashboards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getInsightsDashboardsRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) =>
                this.http
                    .get<InsightsDashboards>(
                        `${SITES_API_URL}/${siteId}/${INSIGHTS_ENDPOINT}/${DASHBOARDS_ENDPOINT}`,
                    )
                    .pipe(
                        map(({ dashboards, url }) =>
                            getInsightsDashboardsSuccessAction({
                                dashboards,
                                url,
                            }),
                        ),
                        catchError(error =>
                            of(
                                getInsightsDashboardsFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    getInsightsGuestToken$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getInsightsGuestTokenRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) =>
                this.http
                    .get<InsightsGuestToken>(
                        `${SITES_API_URL}/${siteId}/${INSIGHTS_ENDPOINT}/${DASHBOARDS_ENDPOINT}/${TOKEN_ENDPOINT}`,
                    )
                    .pipe(
                        retry({
                            count: 8,
                            delay: 15000,
                        }),
                        map(({ token }) =>
                            getInsightsGuestTokenSuccessAction({
                                token,
                            }),
                        ),
                        catchError(error =>
                            of(getInsightsGuestTokenFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    constructor(
        private readonly http: HttpClient,
        private readonly actions$: Actions,
        private readonly store: Store<AppState>,
    ) {}
}
